import { STAGE_ORDER, type Stage } from '@crm/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiClient, type DashboardData } from '../lib/api';
import { formatBps, formatChf, formatChfCompact } from '../lib/format';

const STAGE_LABEL: Record<Stage, string> = {
  identified: 'Identified',
  qualifying: 'Qualifying',
  compliance_review: 'Compliance review',
  proposal: 'Proposal',
  onboarding: 'Onboarding',
  active: 'Active',
};

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getDashboardRm()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxStageAmount = useMemo(() => {
    if (!data) return 1;
    return Math.max(...data.pipeline_by_stage.map((s) => s.total_initial_amount_chf), 1);
  }, [data]);

  const revenueVariancePct = useMemo(() => {
    if (!data || data.targets.revenue_target_chf === 0) return null;
    return (data.revenue_forecast_chf / data.targets.revenue_target_chf - 1) * 100;
  }, [data]);

  if (loading) {
    return (
      <div className="px-8 py-10 max-w-content mx-auto">
        <div className="h-6 w-32 bg-platinum rounded animate-pulse" />
        <div className="h-12 w-80 bg-platinum rounded animate-pulse mt-3" />
        <div className="mt-10 grid grid-cols-3 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card h-32 animate-pulse bg-platinum/40" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-8 py-10 max-w-content mx-auto">
        <p className="label-uppercase">— Error</p>
        <h1 className="mt-2 font-display text-3xl text-graphite">Unable to load dashboard</h1>
        <p className="mt-3 text-sm text-ink-secondary">{error}</p>
      </div>
    );
  }

  return (
    <div className="px-8 py-10 max-w-content mx-auto">
      <header>
        <p className="label-uppercase">— 01 / Dashboard</p>
        <h1 className="mt-2 font-display text-4xl text-graphite font-semibold">
          Pipeline overview
        </h1>
        <p className="mt-2 text-sm text-ink-secondary">
          Live KPIs · audit-trailed · refreshed on every load
        </p>
      </header>

      {/* Row 1 — Hero KPIs */}
      <section className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          label="Active AuM"
          value={formatChfCompact(data.aum_chf)}
          sub="Sum across all active clients"
        />
        <KpiCard
          label="NNM · month-to-date"
          value={formatChfCompact(data.nnm_mtd_chf)}
          sub={`Target ${formatChfCompact(data.targets.nnm_target_chf)} for 2026`}
        />
        <KpiCard
          label="Forecast revenue 2026"
          value={formatChfCompact(data.revenue_forecast_chf)}
          sub={
            revenueVariancePct != null
              ? `${revenueVariancePct >= 0 ? '+' : ''}${revenueVariancePct.toFixed(0)}% vs target`
              : 'No target set'
          }
          tone={
            revenueVariancePct != null
              ? revenueVariancePct < -15
                ? 'warning'
                : 'success'
              : 'neutral'
          }
        />
        <KpiCard
          label="Total potential Y+1"
          value={formatChfCompact(data.total_potential_chf)}
          sub={`RoA target · ${data.targets.roa_target_bps} bps`}
        />
      </section>

      {/* Row 2 — Pipeline + Top opps */}
      <section className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <article className="lg:col-span-3 card p-6">
          <p className="label-uppercase">— Pipeline · 01</p>
          <h2 className="mt-2 font-display text-2xl text-graphite font-semibold">
            Initial expected by stage
          </h2>
          <ul className="mt-6 space-y-4">
            {STAGE_ORDER.map((stage, idx) => {
              const row = data.pipeline_by_stage.find((p) => p.stage === stage);
              const amount = row?.total_initial_amount_chf ?? 0;
              const count = row?.count ?? 0;
              const widthPct = (amount / maxStageAmount) * 100;
              return (
                <li key={stage}>
                  <div className="flex items-baseline justify-between text-xs uppercase tracking-wider text-ink-secondary">
                    <span>
                      <span className="font-display text-base text-ink-tertiary mr-2">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      {STAGE_LABEL[stage]}
                    </span>
                    <span className="font-tabular text-ink">
                      {count} · {formatChfCompact(amount)}
                    </span>
                  </div>
                  <div className="mt-1 h-2 bg-platinum/60 rounded-full overflow-hidden">
                    <div
                      className={
                        'h-full rounded-full ' +
                        (stage === 'active'
                          ? 'bg-semantic-success'
                          : stage === 'onboarding'
                            ? 'bg-graphite'
                            : 'bg-ink-secondary')
                      }
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </article>

        <article className="lg:col-span-2 card p-6">
          <p className="label-uppercase">— Top opportunities · 02</p>
          <h2 className="mt-2 font-display text-2xl text-graphite font-semibold">Top scoring</h2>
          <ol className="mt-5 space-y-3">
            {data.top_opportunities.slice(0, 8).map((o, idx) => (
              <li key={o.id} className="flex items-baseline gap-3">
                <span className="font-display text-base text-ink-tertiary w-5 shrink-0">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <Link
                  to={`/opportunities/${o.id}`}
                  className="text-sm text-ink hover:text-graphite truncate flex-1"
                >
                  {o.name}
                </Link>
                <span className="text-xs font-tabular text-ink-tertiary shrink-0">
                  {formatBps(o.roa_bps)}
                </span>
                <span className="text-sm font-tabular text-ink font-medium shrink-0">
                  {formatChfCompact(o.score_chf)}
                </span>
              </li>
            ))}
            {data.top_opportunities.length === 0 && (
              <li className="text-sm italic text-ink-tertiary">No active opportunities yet.</li>
            )}
          </ol>
        </article>
      </section>

      {/* Row 3 — Alerts */}
      <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <article className="card p-6">
          <div className="flex items-baseline justify-between">
            <p className="label-uppercase">— Alerts · 03</p>
            <span className="text-xs font-tabular text-ink-tertiary">
              {data.alerts.length} flag{data.alerts.length === 1 ? '' : 's'}
            </span>
          </div>
          <h2 className="mt-2 font-display text-2xl text-graphite font-semibold">
            Points to watch
          </h2>
          {data.alerts.length === 0 ? (
            <p className="mt-5 text-sm italic text-ink-tertiary">
              No active alerts. Clean pipeline.
            </p>
          ) : (
            <ul className="mt-5 space-y-3">
              {data.alerts.slice(0, 8).map((alert) => (
                <li
                  key={alert.id}
                  className="flex items-start gap-3 py-2 border-t border-platinum first:border-t-0 first:pt-0"
                >
                  <span className="inline-block w-1.5 h-1.5 mt-2 rounded-full bg-signal shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/opportunities/${alert.opportunity_id}`}
                      className="text-sm text-ink hover:text-graphite font-medium block truncate"
                    >
                      {alert.opportunity_name}
                    </Link>
                    <p className="text-xs text-ink-secondary mt-0.5">{alert.details}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="card p-6">
          <p className="label-uppercase">— Revenue vs target · 04</p>
          <h2 className="mt-2 font-display text-2xl text-graphite font-semibold">Forecast 2026</h2>
          <div className="mt-6">
            <p className="text-4xl font-display font-semibold text-graphite">
              {formatChf(data.revenue_forecast_chf)}
            </p>
            <p className="text-xs font-tabular text-ink-tertiary mt-1">
              of {formatChf(data.targets.revenue_target_chf)} target
            </p>
            <div className="mt-4 h-3 bg-platinum/60 rounded-full overflow-hidden">
              <div
                className={
                  'h-full rounded-full transition-all ' +
                  (revenueVariancePct != null && revenueVariancePct < -15
                    ? 'bg-semantic-warning'
                    : 'bg-semantic-success')
                }
                style={{
                  width:
                    data.targets.revenue_target_chf > 0
                      ? `${Math.min((data.revenue_forecast_chf / data.targets.revenue_target_chf) * 100, 100)}%`
                      : '0%',
                }}
              />
            </div>
            {revenueVariancePct != null && (
              <p className="mt-3 text-sm text-ink-secondary">
                {revenueVariancePct >= 0 ? '+' : ''}
                {revenueVariancePct.toFixed(0)}% vs target · driven by{' '}
                {data.pipeline_by_stage.reduce((acc, p) => acc + p.count, 0)} active opportunities
              </p>
            )}
          </div>
        </article>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'neutral' | 'success' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-semantic-success'
      : tone === 'warning'
        ? 'text-semantic-warning'
        : 'text-ink-tertiary';
  return (
    <div className="card p-5">
      <p className="label-uppercase">— {label}</p>
      <p className="mt-2 font-display text-3xl font-semibold text-graphite leading-none">{value}</p>
      <p className={'mt-2 text-xs font-tabular ' + toneClass}>{sub}</p>
    </div>
  );
}
